import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { type MessageableUser, departmentLabelKey } from '@nbh/shared';
import { api } from '../../lib/api';
import { InitialsAvatar } from './ChatListRow';
import { isSharedAccount, matchesQuery } from './directorySearch';

export interface PeoplePickerProps {
  onClose: () => void;
  onPicked: (person: MessageableUser, conversationId: string) => void;
  onError: (message: string) => void;
}

/**
 * Who to write to.
 *
 * Opening the conversation happens here rather than on the thread screen, because the
 * server decides which conversation this is — asking for one with the same colleague
 * twice returns the existing thread, and the caller must use the id it gives back.
 *
 * A full-screen state, so it has a way out that is not the browser's back gesture: the
 * same back chevron the ticket detail uses. The search field is where arriving here is
 * headed, so it takes focus at once; it filters the directory the client already holds,
 * on name, department and role. Each row says who the person is, not just what they are
 * called — "Abu" is a guess, "Abu · Housekeeping" is a person — and a desk login that
 * several people sit behind is marked so nobody expects one of them in particular.
 */
export function PeoplePicker({ onClose, onPicked, onError }: PeoplePickerProps) {
  const { t } = useTranslation();
  const [people, setPeople] = useState<MessageableUser[] | null>(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .messageDirectory()
      .then(setPeople)
      .catch((caught: Error) => onError(caught.message));
  }, [onError]);

  const matches = useMemo(
    () =>
      (people ?? []).filter((person) =>
        matchesQuery(person, query, t(departmentLabelKey[person.department])),
      ),
    [people, query, t],
  );

  async function pick(person: MessageableUser) {
    if (busy) return;
    setBusy(true);
    try {
      const { id } = await api.startConversation(person.id);
      onPicked(person, id);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : t('messages.startFailed'));
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('messages.pickHeading')}
      className="fixed inset-0 z-50 flex flex-col bg-slate-950 pl-[var(--safe-left)] pr-[var(--safe-right)]"
    >
      {/* Under the status bar, not against it: the same inset the bell drawer takes. */}
      <header className="flex items-center gap-1 border-b border-slate-800 px-1 pb-1 pt-[max(0.25rem,var(--safe-top))]">
        <button
          type="button"
          onClick={onClose}
          className="flex min-h-[48px] items-center gap-1 bg-transparent px-3 text-sm text-slate-400"
        >
          <ChevronLeft className="size-5" aria-hidden="true" />
          {t('messages.back')}
        </button>
      </header>

      <div className="px-4 pb-2 pt-3">
        <h1 className="text-lg font-semibold text-slate-100">{t('messages.pickHeading')}</h1>
        <input
          type="search"
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('messages.search')}
          aria-label={t('messages.search')}
          autoComplete="off"
          // 16px, or iOS zooms the page on focus and never quite zooms back.
          className="mt-3 min-h-[48px] w-full rounded-full border border-slate-700 bg-slate-900 px-4 text-base text-slate-100"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[var(--safe-bottom)]">
        {people === null ? (
          <p className="mt-2 text-sm text-slate-400">{t('messages.loading')}</p>
        ) : people.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">{t('messages.noColleagues')}</p>
        ) : matches.length === 0 ? (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="mt-2 flex min-h-[48px] w-full flex-col items-start justify-center gap-0.5 rounded-lg bg-transparent px-1 text-left"
          >
            <span className="text-sm text-slate-300">{t('messages.noMatch', { query })}</span>
            <span className="text-xs text-brookfields-gold-light">{t('messages.clearSearch')}</span>
          </button>
        ) : (
          <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
            {matches.map((person) => (
              <li key={person.id}>
                <PersonRow person={person} busy={busy} onPick={() => void pick(person)} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function PersonRow({
  person,
  busy,
  onPick,
}: {
  person: MessageableUser;
  busy: boolean;
  onPick: () => void;
}) {
  const { t } = useTranslation();
  const department = t(departmentLabelKey[person.department]);
  const shared = isSharedAccount(person);

  return (
    <button
      type="button"
      onClick={onPick}
      disabled={busy}
      className="flex min-h-[64px] w-full items-center gap-3 bg-transparent px-3 py-2 text-left disabled:opacity-50"
    >
      {/* A desk's disc takes its letters from the team, so it never impersonates a person. */}
      <InitialsAvatar id={person.id} name={shared ? department : person.name} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-slate-100">{person.name}</span>
          {shared && (
            <span
              data-shared="true"
              className="shrink-0 rounded-full border border-slate-700 px-1.5 text-[10px] uppercase tracking-wide text-slate-400"
            >
              {t('messages.shared')}
            </span>
          )}
        </span>
        <span className="block truncate text-xs text-slate-400">{department}</span>
      </span>
    </button>
  );
}
